import { Category, Listing, OfferType, RankType } from '@smg-real-estate/hg-rets-schema';
import { type RouteLocationRaw } from 'vue-router';

import { ContractModel } from '@/api/toplistingService';
import { UserDetails } from '@/api/user';

export enum InsertionStatus {
  Draft = 'DRAFT',
  PendingApproval = 'PENDING_APPROVAL',
  PendingRepublishApproval = 'PENDING_REPUBLISH_APPROVAL',
  Approved = 'APPROVED',
  Published = 'PUBLISHED',
  CONTACT_REQUESTED = 'CONTACT_REQUESTED',
  Rejected = 'REJECTED',
  Archived = 'ARCHIVED',
  Archiving = 'ARCHIVING',
  Republishing = 'REPUBLISHING',
  LegacyMigrated = 'LEGACY_MIGRATED',
  Deleted = 'DELETED',
}

export enum Currency {
  CHF = 'CHF',
  EUR = 'EUR',
  USD = 'USD',
}

export enum AvailableFrom {
  BY_AGREEMENT = 'AGREEMENT',
  IMMEDIATELY = 'IMMEDIATELY',
  DATE = 'DATE',
}

export enum SupportedLanguage {
  DE = 'de',
  EN = 'en',
  FR = 'fr',
  IT = 'it',
}

export enum PagesFlowOrder {
  DETAILS,
  BUNDLE,
  BILLING,
  CONFIRMATION,
}

export type ListingUrls = Record<SupportedLanguage, string>;

export enum CustomerSegment {
  PROFESSIONAL = 'professional',
}

export interface Insertion {
  agencyId?: string;
  id: string;
  status: InsertionStatus;
  userId: string;
  username: string;
  flaggedForFraud?: string;
  listingUrls?: ListingUrls;
  listing: Listing;
  completenessScore?: number;
  createdAt: Date;
  rejectionMessage?: string;
  lastSavedHash: string;
  lastSavedAt?: Date;
  firstPublishedDate?: string;
  renewalPaymentDueDate?: Date;
  publishedInsertionsByOwner?: number;
  isContactEmailReused?: boolean;
  paymentType?: string;
  directPaymentInfo?: any;
  customerSegment?: CustomerSegment.PROFESSIONAL;
  furthestPage?: string;
  objectReference: string;
  toplistingData?: ToplistingData;
  updatedAt?: string;
  shouldHideAddress?: boolean;
}

export interface ListingPerformanceSummary {
  propertyReferenceId: string;
  totalImpressions: number | null;
  totalViews: number | null;
  totalLeads: number | null;
  performanceScore: number;
}

export interface QueryData {
  offer: 'SALE' | 'RENT';
  category: string;
  type: string;
  country: string;
  zip: string;
  city: string;
  street?: string;
  canton?: string;
  userid?: string;
  'pricing-campaign-id'?: string;
  listingOfferType?: OfferType;
  listingCategoriesMapped?: Category[];
}

export enum UserGroup {
  PRIVATE = 'private',
  AGENCY = 'agency',
  CUSTOMER_SERVICE_AGENT = 'ccc',
  PERSONAL = 'personal',
}

export enum UserTypeLogin {
  SMG_INTERNAL = 'smg_internal',
  AGENCY = 'agency',
}

export enum UserSalutation {
  MR = 'Mr.',
  MS = 'Ms.',
  NOT_SPECIFIED = 'Not specified',
}

export enum UserAreaOfActivity {
  REAL_ESTATE_MARKETING = 'real-estate-marketing',
  REAL_ESTATE_SALES = 'real-estate-sales',
  PROPERTY_MANAGEMENT = 'property-management',
  PROJECT_DEVELOPMENT = 'project-development',
  COMMERCIAL_REAL_ESTATE = 'commercial-real-estate',
  OTHER = 'other',
  NO_INFORMATION_PROVIDED = 'no-information-provided',
}

export type UserId = string;

export interface AuthorizedUser {
  userId: UserId;
  auth0Id: string;
  email: string;
  logonId: string;
  groups: UserGroup[];
  mfaSkipped?: boolean;
  picture?: string;
  name?: string;
  userType?: UserType;
  sourcePlatform?: SourcePlatform;
  userDetails?: UserDetails;
}

export interface NifUser {
  ownerId: string;
  contactEmails: string[];
  updatedAt: string;
  sourcePlatform: SourcePlatform;
  userType: UserType;
  autoSegmentation: boolean;
}

export interface InvoiceData {
  email?: string;
}

export interface CloudinarySignedUploadParams {
  signature: string;
  timestamp: string;
  api_key: string;
  public_id: string;
  uploadUrl: string;
}

export enum PaymentType {
  DIRECT = 'DIRECT',
  INVOICE = 'INVOICE',
}

export interface BookToplistingRequest {
  rankType: RankType;
  beginRequest: string;
  endRequest: string | null;
  contractModel: ContractModel.DAILY | ContractModel.ABO | ContractModel.LIFETIME;
  sourceSystem: 'BUSINESS_MANAGER';
}

export interface BookToplistingResponse {
  message: string;
  toplisting?: any;
  validationErrors?: string[];
  providedData?: any;
}

export enum ToplistingModalAction {
  CREATE = 'CREATE',
  EDIT = 'EDIT',
}

export interface ToplistingStatus {
  toplistingVariant: ToplistingVariant;
  contractModel?: ContractModel;
  beginRequest: string;
  endRequest: string | null;
}

export enum ToplistingVariant {
  Top = 'TOP', // rankType: TOP, contractModel: DAILY
  Lifetime = 'LIFETIME', // rankType: TOP, contractModel: LIFETIME
  Premium = 'PREMIUM', // rankType: PREMIUM, contractModel: DAILY
  TopAbo = 'TOP_ABO', // rankType: TOP, contractModel: ABO
}

export enum ToplistingEventType {
  ACTIVATE_TOPLISTING = 'ACTIVATE_TOPLISTING',
  SCHEDULE_TOPLISTING = 'SCHEDULE_TOPLISTING',
}

export enum ToplistingSourceSystem {
  IDX = 'IDX',
  MYHOMEGATE = 'MYHOMEGATE',
  IMPORTER = 'IMPORTER',
  TLABO_UI = 'TLABO_UI',
  HG_PLUS = 'HG_PLUS',
  TOPLISTING = 'TOPLISTING',
  BUSINESS_MANAGER = 'BUSINESS_MANAGER',
  AUTOMATIC = 'AUTOMATIC',
  RULE_AUTOMATIC = 'RULE_AUTOMATIC',
  PROJECT_LIGHT = 'PROJECT_LIGHT',
}

export interface ToplistingData {
  eventType: ToplistingEventType;
  listingType: RankType;
  contractModel: ContractModel;
  beginRequest: string;
  sourceSystem: ToplistingSourceSystem;
  endRequest?: string;
}

export enum ListingInsightInfoName {
  SEARCH_POSITION = 'search_position',
  IMPRESSIONS = 'impressions',
  LEADS = 'leads',
  PERFORMANCE = 'performance',
  VIEWS = 'views',
  TOP_10_RESULTS = 'top_10_results',
  SIMILAR_LISTINGS = 'similar_listings',
}

export const LISTING_INSIGHT_I18N_KEYS: Record<string, string> = {
  [ListingInsightInfoName.SEARCH_POSITION]: 'searchPosition',
  [ListingInsightInfoName.IMPRESSIONS]: 'impressions',
  [ListingInsightInfoName.VIEWS]: 'views',
  [ListingInsightInfoName.LEADS]: 'leads',
  [ListingInsightInfoName.PERFORMANCE]: 'performance',
  [ListingInsightInfoName.TOP_10_RESULTS]: 'top10Results',
};

export interface ListingInsightInfo {
  icon: any; // as domus icons also have 'any' type...
  title: string;
  info: string;
  value?: string | number;
  name: ListingInsightInfoName;
  trackAction?: string;
  displayNumberAsTrafficLight?: boolean;
  isLeads?: boolean;
  totalCallLeads?: string | number;
  totalFormLeads?: string | number;
}

export enum LandingFormStep {
  CUSTOMER_SEGMENT,
  OFFER_TYPE,
  CATEGORY,
  TYPE,
  GEO_SECTION,
}

/**
 * AgencyAgent from customer-service
 */
export enum LoadState {
  UNDEFINED,
  LOADING,
  LOADED,
  ERROR,
}

/**
 * User from Insertion Funnel API
 */
export interface User {
  ownerId: string;
  userType: UserType;
}

/**
 * User Type from Insertion Funnel API
 */
export enum UserType {
  PRIVATE = 'PRIVATE',
  SEMI_PRO = 'SEMI_PRO',
}

export enum SourcePlatform {
  HOMEGATE = 'homegate',
  IMMOSCOUT24 = 'immoscout24',
  SMG = 're.smg',
}

// Listing Quality Score

export interface ListingCompletenessScoreDetails {
  image: {
    isPotentialMaxScore: boolean;
    count: number;
  };
  movie: {
    isPotentialMaxScore: boolean;
  };
  document: {
    isPotentialMaxScore: boolean;
  };
  virtualTour: {
    isPotentialMaxScore: boolean;
  };
  description: {
    isPotentialMaxScore: boolean;
    count: number;
  };
  price: {
    isPotentialMaxScore: boolean;
  };
  address: {
    isPotentialMaxScore: boolean;
  };
}

export interface ListingQualityScore {
  listingCompletenessScore: number;
  listingCompletenessScoreDetails: ListingCompletenessScoreDetails;
}

export type ImageFileExtension = 'jpg' | 'jpeg' | 'png' | 'heic' | 'gif';

export type DocumentFileExtension = 'pdf';

export type SupportedFileExtensions = ImageFileExtension | DocumentFileExtension;

export enum DownloadState {
  UNKNOWN = 'UNKNOWN',
  LOADING = 'LOADING',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
}

export interface BreadcrumbLink {
  to: RouteLocationRaw;
  label: string;
  clickable: boolean;
}

export type FileFormat = 'xlsx' | 'csv';

export enum ContentType {
  'xlsx' = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'csv' = 'text/csv',
  'json' = 'application/json',
}

export interface FiltersWithCount {
  total: number;
  filters: FilterWithCount[];
}

export interface FilterWithCount {
  fieldName: string;
  values: ValueWithCount[];
}

export interface ValueWithCount {
  value: string;
  count: number;
}

export interface RawIndustryAssociation {
  associationId: string;
  name: string;
  parent: string;
  type: AssociationType;
}

export interface IndustryAssociation {
  associationId: string;
  name: string;
  selected: boolean;
  parent?: string;
  type?: AssociationType;
  logo?: string;
  favourite?: boolean;
  children?: IndustryAssociation[];
}

export enum AssociationType {
  SPECIALIST = 'specialist',
  MEMBER = 'member',
}

export type ListingModalAction = 'deactivate' | 'delete' | 'toplisting';

export type DiagnosticReportSeverity = 'high' | 'medium' | 'low';

export interface DiagnosticReportKeyCorrelation {
  severity: DiagnosticReportSeverity;
  description: string;
}

export interface DiagnosticReport {
  healthScore: number;
  managementSummary: string;
  keyCorrelations: DiagnosticReportKeyCorrelation[];
  holisticReasoning: string;
  generatedAt: string;
}
